import { useState, useEffect, useCallback } from "react";
import { Formik, Form } from "formik";
import { useToast, Spinner } from "@chakra-ui/react";
import DOMPurify from "dompurify";
import { InstructionsInfoBox } from "newui/components/ui/InstructionsInfoBox";
import { FormikRichTextEditor } from "newui/components/ui/RichTextEditor/FormikRichTextEditor";
import { CheckboxWithLabel } from "newui/components/ui/CheckboxWithLabel";
import { Button } from "newui/components/Button";
import type { Company } from "newui/features/Company/types";
import { DisclaimerModal } from "newui/features/Rfx/Evaluation/components/DisclaimerModal";
import type { DisclaimerCompany } from "newui/features/Rfx/Evaluation/types/panel.type";
import { useDisclaimerSettings } from "../../hooks/useDisclaimerSettings";
import { useSaveDisclaimerSettings } from "../../hooks/useSaveDisclaimerSettings";
import { ReadOnlyGuard } from "newui/features/ExecutiveView";

interface PanelDisclaimerSettingsProps {
	company: Company;
}

interface DisclaimerFormValues {
	disclaimer_text: string;
}

const DUMMY_COMPANIES: DisclaimerCompany[] = [
	{ id: 0, name: "Acme Corp", logo_url: null },
	{ id: 0, name: "BuildRight Ltd", logo_url: null },
	{ id: 0, name: "DesignPro Inc", logo_url: null },
];

export const PanelDisclaimerSettings: React.FC<PanelDisclaimerSettingsProps> = ({
	company,
}) => {
	const toast = useToast();
	const { data, isLoading } = useDisclaimerSettings();
	const saveMutation = useSaveDisclaimerSettings();

	const [enabled, setEnabled] = useState(false);
	const [isPreviewOpen, setIsPreviewOpen] = useState(false);
	const [previewHtml, setPreviewHtml] = useState("");

	useEffect(() => {
		if (data) {
			setEnabled(data.disclaimer_enabled);
		}
	}, [data]);

	// Always populate the editor with the resolved text (default or custom).
	// The API compares against the default hash on save — if unchanged it stores NULL.
	const initialText = data?.disclaimer_text ?? "";

	const showSuccess = useCallback(
		(description: string) => {
			toast({
				title: "Settings updated",
				description,
				status: "success",
				duration: 3000,
				isClosable: true,
			});
		},
		[toast]
	);

	const showError = useCallback(() => {
		toast({
			title: "Error",
			description: "Failed to save disclaimer settings. Please try again.",
			status: "error",
			duration: 4000,
			isClosable: true,
		});
	}, [toast]);

	const handleSave = useCallback(
		(values: DisclaimerFormValues) => {
			saveMutation.mutate(
				{
					disclaimer_enabled: enabled,
					disclaimer_text: values.disclaimer_text,
				},
				{
					onSuccess: () => showSuccess("Disclaimer settings have been saved."),
					onError: () => showError(),
				}
			);
		},
		[enabled, saveMutation, showSuccess, showError]
	);

	const handleReset = useCallback(
		(resetForm: (state: { values: DisclaimerFormValues }) => void) => {
			saveMutation.mutate(
				{
					disclaimer_enabled: enabled,
					disclaimer_text: null,
				},
				{
					onSuccess: (result) => {
						// Reset the editor to the default text returned by the API
						resetForm({ values: { disclaimer_text: result?.disclaimer_text ?? "" } });
						showSuccess("Disclaimer reset to the default text.");
					},
					onError: () => showError(),
				}
			);
		},
		[enabled, saveMutation, showSuccess, showError]
	);

	const handlePreview = useCallback(
		(values: DisclaimerFormValues) => {
			setPreviewHtml(DOMPurify.sanitize(values.disclaimer_text));
			setIsPreviewOpen(true);
		},
		[]
	);

	if (isLoading) {
		return (
			<div className="flex items-center justify-center py-12">
				<Spinner size="lg" color="blue.500" />
			</div>
		);
	}

	return (
		<div data-testid="panel-disclaimer-settings">
			<InstructionsInfoBox inline data-testid="disclaimer-settings-info">
				Set a disclaimer that panel members must acknowledge before evaluating
				submissions. Leave it disabled to use the platform default for{" "}
				{company?.name ?? "your company"}.
			</InstructionsInfoBox>

			<div
				className="mt-6"
				data-testid="disclaimer-settings-enabled-checkbox"
			>
				<CheckboxWithLabel
					name="disclaimer_enabled"
					label="Enable panel member disclaimer"
					checked={enabled}
					onChange={(e) => setEnabled(e.target.checked)}
				/>
			</div>

			<Formik<DisclaimerFormValues>
				initialValues={{ disclaimer_text: initialText }}
				enableReinitialize
				onSubmit={handleSave}
			>
				{({ values, resetForm }) => (
					<Form className="mt-6 space-y-4">
						<FormikRichTextEditor
							name="disclaimer_text"
							label="Disclaimer text"
							placeholder="Enter the disclaimer panel members must acknowledge..."
							testId="disclaimer-settings-editor"
						/>

						<div className="flex flex-wrap gap-2">
							<ReadOnlyGuard>
								<Button
									type="submit"
									btntype="primary"
									isLoading={saveMutation.isPending}
									data-testid="disclaimer-settings-save-btn"
								>
									Save
								</Button>
							</ReadOnlyGuard>
							<Button
								type="button"
								btntype="secondary"
								onClick={() => handleReset(resetForm)}
								data-testid="disclaimer-settings-reset-btn"
							>
								Reset to default
							</Button>
							<Button
								type="button"
								btntype="secondary"
								onClick={() => handlePreview(values)}
								data-testid="disclaimer-settings-preview-btn"
							>
								Preview
							</Button>
						</div>
					</Form>
				)}
			</Formik>

			<DisclaimerModal
				isOpen={isPreviewOpen}
				onClose={() => setIsPreviewOpen(false)}
				rfxId={0}
				evalId={0}
				memberId={0}
				isPreview
				previewData={{
					disclaimer_text: previewHtml,
					companies: DUMMY_COMPANIES,
				}}
			/>
		</div>
	);
};
